import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { navigate } from '../lib/router';
import { landingPage, verifyOtp, type CodeDelivery } from '../stores/session';
import { errorMessage, Field, FormError, Heading, PrimaryButton } from './AuthForm';
import { useNext } from './OtpStep';

/**
 * Staff sign-in, second step: the 6-digit code Joe IT Bot sent to the Telegram the person linked in the
 * CRM (STAFF_TELEGRAM_PLAN.md). When the code can't be delivered, says why; someone who has an
 * authenticator app on may then use it instead (`onAuthenticator`).
 */
export function TelegramStep(props: {
  challengeId: string;
  telegram: CodeDelivery;
  authenticatorFallback: boolean;
  onAuthenticator: () => void;
  onBack: () => void;
}) {
  const next = useNext();
  const [delivery, setDelivery] = useState(props.telegram);
  const [fallback, setFallback] = useState(props.authenticatorFallback);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [wait, setWait] = useState(props.telegram.resendIn ?? 30);

  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (code.length !== 6 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const changePassword = await verifyOtp(props.challengeId, code);
      navigate(changePassword ?? landingPage(next), { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setCode('');
      setBusy(false);
    }
  }

  async function resend() {
    setError(null);
    setNotice(null);
    try {
      const r = await api.post<{ telegram: CodeDelivery; authenticatorFallback: boolean }>('/api/auth/resend-code', { challengeId: props.challengeId });
      setDelivery(r.telegram);
      setFallback(r.authenticatorFallback);
      setWait(r.telegram.resendIn ?? (r.telegram.sent ? 30 : 0));
      if (r.telegram.sent) {
        setCode('');
        setNotice('We sent a new code.');
      }
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function useAuthenticator() {
    setError(null);
    try {
      await api.post('/api/auth/use-authenticator', { challengeId: props.challengeId });
      props.onAuthenticator();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  const mmss = `${Math.floor(wait / 60)}:${String(wait % 60).padStart(2, '0')}`;
  return (
    <>
      <Heading title="Check Telegram" subtitle="Enter your sign-in code" />
      {delivery.sent ? (
        <p className="mt-3.5 text-[15px] text-muted">
          We sent a 6-digit code to <span className="font-semibold text-accent-fg">{delivery.to ?? 'your Telegram'}</span> from Joe IT Bot. It is valid for 5
          minutes.
        </p>
      ) : (
        <p className="mt-4 rounded-2xl bg-warn/10 px-4 py-3 text-[14px] text-warn" role="alert">
          {delivery.message ?? 'We couldn’t send your Telegram code. Try again in a moment.'}
        </p>
      )}

      <form onSubmit={submit} noValidate className="mt-8">
        <Field
          id="tg-code"
          label="Code from Telegram"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(v) => setCode(v.replace(/\D/g, '').slice(0, 6))}
          inputClassName="num tracking-[0.5em]"
          autoFocus
        />

        <div className="mx-1 mb-[22px] mt-1.5 flex flex-wrap justify-between gap-2 text-sm">
          <button type="button" onClick={props.onBack} className="font-medium text-muted hover:text-fg">
            ← Back
          </button>
          {wait > 0 ? (
            <span className="text-muted">Send a new code in {mmss}</span>
          ) : (
            <button type="button" onClick={resend} className="font-semibold text-accent-fg">
              {delivery.sent ? 'Didn’t get it? Send a new code' : 'Send a new code'}
            </button>
          )}
        </div>

        {notice && !error && <p className="mb-3 px-1 text-sm text-up">{notice}</p>}
        <FormError message={error} />
        <PrimaryButton disabled={code.length !== 6 || busy} busy={busy} busyLabel="Signing in…">
          Sign in
        </PrimaryButton>
        {fallback && (
          <button type="button" onClick={useAuthenticator} className="mt-4 w-full text-center text-sm font-semibold text-accent-fg">
            Use authenticator app instead
          </button>
        )}
      </form>
    </>
  );
}
